<template>
  <div class="agency-table">
    <div class="agency-table__bar">
      <span class="agency-table__title">{{ text.agencyTableTitle }}</span>
      <span class="agency-table__spacer"></span>
      <el-input
        v-model="query"
        class="agency-table__q"
        size="mini"
        clearable
        :placeholder="text.agencyNmLabel"
        @keyup.enter.native="$emit('search', query)"
        @clear="$emit('search', '')" />
      <el-button size="mini" icon="el-icon-search" @click="$emit('search', query)">{{ text.searchAction }}</el-button>
      <el-button
        v-if="can.add"
        size="mini"
        type="primary"
        plain
        icon="el-icon-plus"
        :disabled="!operator"
        @click="$emit('add')"
        >{{ text.agencyAddAction }}</el-button
      >
      <el-button v-if="can.import" size="mini" icon="el-icon-upload2" @click="$emit('import')">{{
        text.agencyImportAction
      }}</el-button>
    </div>

    <el-alert v-if="!operator" :title="text.agencyPickOperatorFirst" type="info" :closable="false" show-icon />

    <el-table
      v-else
      class="agency-table__table"
      :data="agencies"
      size="mini"
      border
      v-loading="loading"
      :empty-text="text.agencyEmpty"
      :row-class-name="rowClass">
      <!-- 이름과 코드가 한 칸이다 — 둘을 따로 세우면 1440 에서 오른쪽 열(상태·관리)이 잘린다(§2-5) -->
      <el-table-column :label="text.agencyNmLabel" min-width="150">
        <template slot-scope="scope">{{ nameOf(scope.row) }}</template>
      </el-table-column>
      <el-table-column :label="text.mgrNmLabel" width="120">
        <template slot-scope="scope">
          <div>{{ scope.row.mgrNm }}</div>
          <div class="agency-table__sub">{{ scope.row.mobile }}</div>
        </template>
      </el-table-column>
      <!-- 날짜와 시각을 화면이 정한 자리에서 끊는다([stampView]) — 한 문자열이면 전역 `word-break` 가 숫자 안에서 끊는다 -->
      <el-table-column :label="text.issuedAtColumn" width="100">
        <template slot-scope="scope">
          <div>{{ stampOf(scope.row.issuedAt).date }}</div>
          <div class="agency-table__sub">{{ stampOf(scope.row.issuedAt).time }}</div>
        </template>
      </el-table-column>
      <el-table-column :label="text.stateColumn" width="70" align="center">
        <template slot-scope="scope">
          <el-tag size="mini" :type="scope.row.useYn === 'N' ? 'info' : 'success'">{{
            scope.row.useYn === 'N' ? text.inactiveTag : text.activeTag
          }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="text.storeCountColumn" width="70" align="right">
        <template slot-scope="scope">
          <el-button size="mini" type="text" @click="$emit('drill-down', scope.row)">{{
            scope.row.storeCount != null ? scope.row.storeCount : '-'
          }}</el-button>
        </template>
      </el-table-column>
      <el-table-column :label="text.accountCountColumn" width="70" align="right">
        <template slot-scope="scope">{{ scope.row.accountCount != null ? scope.row.accountCount : '-' }}</template>
      </el-table-column>
      <el-table-column :label="$t('관리')" width="100">
        <template slot-scope="scope">
          <el-button v-if="can.up" size="mini" type="text" @click="$emit('edit', scope.row)">{{
            text.editAction
          }}</el-button>
          <el-button
            v-if="can.off && scope.row.useYn !== 'N'"
            size="mini"
            type="text"
            @click="$emit('deactivate', scope.row)"
            >{{ text.agencyOffAction }}</el-button
          >
          <el-button
            v-if="can.on && scope.row.useYn === 'N'"
            size="mini"
            type="text"
            @click="$emit('reactivate', scope.row)"
            >{{ text.agencyOnAction }}</el-button
          >
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'
import { CALLPOP_ACTION, isInactiveOrg, orgLabel, stampView } from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'

/**
 * 고른 운영사 아래 관리점 표 — 화면 #1 의 오른쪽 판.
 *
 * 소속 매장 수는 **누르면 드릴다운**이다(§2 #1 「행 클릭 = 드릴다운」) — 매장 입점 현황 화면을 그 관리점
 * 필터로 연다. 버튼 가시성은 세션 액션이 정한다(역할 문자열 비교 없음).
 */
export default {
  name: 'AgencyTable',

  props: {
    /** 고른 운영사 행 — 없으면 표 대신 안내를 보인다 */
    operator: { type: Object, default: null },
    agencies: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },

  data() {
    return { text: CALLPOP_TEXT, query: '' }
  },

  computed: {
    can() {
      return {
        add: checkAuthActionAccess(CALLPOP_ACTION.AGENCY_ADD),
        up: checkAuthActionAccess(CALLPOP_ACTION.AGENCY_UP),
        off: checkAuthActionAccess(CALLPOP_ACTION.AGENCY_OFF),
        on: checkAuthActionAccess(CALLPOP_ACTION.AGENCY_ON),
        import: checkAuthActionAccess(CALLPOP_ACTION.AGENCY_IMPORT),
      }
    },
  },

  methods: {
    /** 「이름 (코드)」 — 운영사 목록과 같은 낱말 짓기다(`orgLabel` 한 곳) */
    nameOf(row) {
      return orgLabel(row && row.agencyNm, row && row.code)
    },
    /** 발급일의 날짜·시각 조각 — 끊는 자리를 화면이 정한다 */
    stampOf(value) {
      return stampView(value)
    },
    /** 비활성 행은 흐리게 — 「비활성 포함」을 켰을 때만 온다(R-A). 상태 칸의 태그가 「비활성」을 말한다 */
    rowClass({ row }) {
      return isInactiveOrg(row) ? 'agency-table__row--inactive' : ''
    },
  },
}
</script>

<style scoped>
.agency-table__bar {
  align-items: center;
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}
.agency-table__title {
  font-size: 14px;
  font-weight: 600;
}
.agency-table__spacer {
  flex: 1;
}
.agency-table__q {
  width: 200px;
}
.agency-table__sub {
  color: #909399;
  font-size: 12px;
}
</style>

<style>
/* 행 클래스는 el-table 안쪽 요소라 scoped 로 닿지 않는다(엑셀 다이얼로그의 오류 행과 같은 방식) */
.agency-table__row--inactive td {
  color: #c0c4cc;
}
</style>
